import { toast } from '@/components/toast';
import { createKnowledgeDocument, deleteKnowledgeDocument } from '@/services/api';
import { logger } from '@/services/logger';
import { UploadingFile } from '@/state/agent';
import { IKnowledgeDocument } from '@d-id/types';
import { AxiosError } from 'axios';
import { MutationFunction, useMutation } from 'react-query';

const RETRIES = 3;

export function useUpdateKnowledgeDocuments() {
    const editKnowledgeDocumentsMut: MutationFunction<
        void,
        { knowledgeId: string; uploadingFiles: UploadingFile[]; deletedFiles: UploadingFile[] }
    > = async ({ knowledgeId, uploadingFiles, deletedFiles }) => {
        await Promise.all([
            ...((deletedFiles?.length &&
                deletedFiles.map(async (deletedFile: UploadingFile) => {
                    if (deletedFile?.id) {
                        const knowledgeId = deletedFile.id.split('#');
                        await deleteKnowledgeDocument(knowledgeId[0], knowledgeId[1]);
                    }
                })) ||
                []),
            ...((uploadingFiles?.length &&
                uploadingFiles.map(async (uploadingFile: UploadingFile) => {
                    if (!uploadingFile.id) {
                        const knowledgeDocument: IKnowledgeDocument = {
                            documentType: uploadingFile.file.type,
                            title: uploadingFile.file.name,
                            source_url: uploadingFile.s3Url as string,
                        };
                        await createKnowledgeDocument(knowledgeId, knowledgeDocument);
                    }
                })) ||
                []),
        ]);
    };

    return useMutation('edit-agent', editKnowledgeDocumentsMut, {
        retry(failureCount, error: AxiosError) {
            return (
                failureCount < RETRIES &&
                !!error.response?.status &&
                ([401, 403].some(status => error.response!.status === status) || error.response.status >= 500)
            );
        },
        onError: async (error: any) => {
            logger.error('Could not update documents', { error });
            toast.error({ title: `Could not update documents`, toastId: `knowledge-documents-update-error` });
        },
    });
}
